import { debounceTime, FooterButtons, Options } from '@fl/cmsch-fe-library';
import { debounce } from 'lodash/fp';
import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { tCommon, useOurTranslation } from 'app/translations';
import { AsyncValidationSource } from 'utils/async-validation-source';
import { createAsyncValidationHandler, formHelpers, useForm } from 'utils/forms';
import { validate } from './validate';
import { adminAction } from '../../model';
import { simpleAdminSelector } from '../../model/selector';
import { CreateOrEditMode } from '../../types/permission-form-mode';
import { createOrUpdatePermissionFormName, createOrUpdatePermissionFormValues } from '../../types/permission-form-values';
interface Props {
  /** Permission id when editing, or `null` for create mode. */
  permissionId: number | null;
  /** Create or edit mode. */
  mode: CreateOrEditMode;
  /** Discriminator options available in the form. */
  discriminatorOptions: Options<number>;
  /** Called when the form is submitted. */
  onSubmit(): void;
  /** Called when the form is cancelled. */
  onCancel(): void;
}
const PermissionFormBase: FC<Props> = ({
  onSubmit,
  onCancel,
  mode,
  permissionId,
  discriminatorOptions
}) => {
  const tableData = useSelector(simpleAdminSelector.permissionDetailsTableData);
  const dispatch = useDispatch();
  const {
    t
  } = useOurTranslation('admin');
  const {
    Form,
    Fields,
    submitting,
    valid,
    pristine,
    asyncValidating
  } = useForm({
    form: createOrUpdatePermissionFormName,
    initialValues: createOrUpdatePermissionFormValues,
    destroyOnUnmount: true,
    onSubmit,
    validate
  });
  const isEdit = mode === 'edit' && permissionId !== null;
  const initFormData = useMemo(() => tableData.filter(perm => perm.id === permissionId)[0], []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEdit && initFormData) {
      dispatch(formHelpers.initialize(createOrUpdatePermissionFormName, initFormData));
    }
  }, [dispatch, initFormData, isEdit, tableData]);
  const validateData = useCallback((source: AsyncValidationSource) => {
    dispatch(adminAction.validatePermission(initFormData, mode, source));
  }, [dispatch, initFormData, mode]);
  const validateDebounced = useMemo(() => debounce(debounceTime)(validateData), [validateData]);
  return <Form data-sentry-element="Form" data-sentry-component="PermissionFormBase" data-sentry-source-file="index.tsx">
            <Fields.Input name="code" type="text" label={t('code')} disabled={submitting} autoFocus onFieldChange={createAsyncValidationHandler(validateDebounced, 'code')} isRequired data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            <Fields.Input name="controllerName" type="text" label={t('controller')} disabled={mode === 'edit'} onFieldChange={createAsyncValidationHandler(validateDebounced, 'controllerName')} isRequired data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            <Fields.Input name="actionName" type="text" label={t('action')} disabled={mode === 'edit'} onFieldChange={createAsyncValidationHandler(validateDebounced, 'actionName')} isRequired data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            <Fields.Input name="comment" type="text" label={t('comment')} disabled={submitting} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            <Fields.NumberSelect name="discriminatorId" label={t('discriminator')} options={discriminatorOptions} disabled={submitting} isRequired data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            <Fields.Checkbox name="userEnable" label={t('userEnable')} disabled={submitting} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            <FooterButtons submitting={submitting} valid={valid} pristine={pristine} withoutDivider onCancel={onCancel} asyncValidating={asyncValidating} submitTitle={isEdit ? tCommon('save') : tCommon('create')} data-sentry-element="FooterButtons" data-sentry-source-file="index.tsx" />
        </Form>;
};

/**
 * Admin permission form.
 *
 * @example
 * <PermissionForm
 *   permissionId={123}
 *   mode="edit"
 *   discriminatorOptions={discriminatorOptions}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 * />
 */
export const PermissionForm = memo(PermissionFormBase);